<template>
  <app-cells :indent="false" position="end" gap="8px 12px" class="funding-invoice-export" :aria-busy="busy || (observing && !paused)">
    <span :id="messageId" class="funding-invoice-export__status" aria-live="polite">{{ message }}</span>
    <app-button :disabled="disabled" :aria-describedby="messageId" @click.native="exportInvoice">{{ buttonLabel }}</app-button>
  </app-cells>
</template>

<script>
import {
  getTransferPricingFundingInvoiceBatch,
  postTransferPricingFundingInvoiceExport,
  getTransferPricingFundingInvoiceExport,
  getTransferPricingFundingInvoiceFile,
} from '@/http'
import { downloadInvoicePdf, invoiceErrorMessage, invoicePdf } from './invoiceFile'

const OBSERVATION_LIMIT = 15 * 60 * 1000
const STATUS_REQUEST_TIMEOUT = 30000
const RETRY_DELAYS = [5000, 10000, 20000]
const WAITING_MESSAGE = 'Generation may still be running. Check status again later.'

function attemptFingerprint(invoice) {
  return invoice ? JSON.stringify([invoice.id, invoice.status, invoice.attempt_count, invoice.last_attempt_at, invoice.next_attempt_at, invoice.finished_at]) : null
}

export default {
  name: 'FundingInvoiceExport',
  props: { payment: { type: Object, required: true }, paymentBusy: Boolean },
  data() {
    return {
      invoice: null,
      discovering: false,
      downloading: false,
      checking: false,
      postUncertain: false,
      retryBaseline: null,
      paused: false,
      notice: '',
      generation: 0,
      timer: null,
      deadlineTimer: null,
      readTimer: null,
      checkSequence: 0,
      deadline: 0,
      pollFailures: 0,
      downloadRequested: false,
    }
  },
  computed: {
    expectedStage() {
      if (this.payment.numbering_scheme !== 'confirm_v2') return null
      return this.payment.status === 'confirmed' ? 'numbered' : 'draft'
    },
    documentContext() { return `${this.payment.id}-${this.expectedStage || 'legacy'}` },
    documentLabel() { return this.expectedStage === 'draft' ? 'Draft PDF' : 'Invoice PDF' },
    active() {
      return !!this.invoice && (['pending', 'rendering'].includes(this.invoice.status) ||
        (this.invoice.status === 'failed' && !!this.invoice.next_attempt_at))
    },
    observing() { return this.discovering || this.active },
    busy() { return this.downloading || this.checking },
    canCheck() { return this.paused && (this.discovering || !!this.invoice) },
    messageId() { return `funding-invoice-status-${this.payment.id}` },
    emptyDraft() { return this.payment.status === 'draft' && Array.isArray(this.payment.lines) && !this.payment.lines.length },
    creationBlocked() {
      if (this.invoice) return ''
      if (this.payment.status === 'cancelled') return 'A new invoice cannot be exported for a cancelled payment.'
      if (!['draft', 'confirmed'].includes(this.payment.status)) return 'Invoice export is unavailable for this payment.'
      const recipient = this.payment.recipient && typeof this.payment.recipient === 'object' ? this.payment.recipient.code : this.payment.recipient
      if ((recipient && !['ALTEZZA_TRAVELLING_LTD', 'ALTURA_EXPEDITIONS'].includes(recipient)) || (this.payment.currency && this.payment.currency !== 'USD')) {
        return 'Invoice export is available only for Altezza or Altura payments in USD.'
      }
      return ''
    },
    disabled() { return this.paymentBusy || this.emptyDraft || this.busy || (!this.canCheck && (this.observing || !!this.creationBlocked)) },
    buttonLabel() {
      if (this.downloading) return this.expectedStage ? `Downloading ${this.documentLabel}…` : 'Downloading…'
      if (this.canCheck) return this.checking ? 'Checking status…' : 'Check status'
      if (this.discovering) return this.expectedStage ? `Exporting ${this.documentLabel}…` : 'Exporting…'
      if (this.invoice) {
        if (this.invoice.status === 'pending') return 'Queued…'
        if (this.invoice.status === 'rendering') return this.expectedStage ? `Generating ${this.documentLabel}…` : 'Generating PDF…'
        if (this.invoice.status === 'failed') return this.invoice.next_attempt_at ? 'Waiting for retry…' : (this.expectedStage ? `Retry ${this.documentLabel}` : 'Retry export')
      }
      if (this.expectedStage) return `${this.invoice && this.invoice.status === 'ready' ? 'Download' : 'Export'} ${this.documentLabel}`
      return 'Export'
    },
    message() {
      if (this.emptyDraft) return 'Add tours before exporting an invoice.'
      if (this.notice) return this.notice
      if (this.discovering) return this.expectedStage ? `Checking ${this.documentLabel} export status…` : 'Checking invoice export status…'
      if (!this.invoice) return this.creationBlocked
      if (this.invoice.status === 'failed') {
        if (this.invoice.next_attempt_at) return 'Waiting for an automatic export retry.'
        return `Unable to generate ${this.expectedStage ? this.documentLabel : 'the invoice'}.${this.invoice.error_code ? ` Error code: ${this.invoice.error_code}` : ''}`
      }
      if (this.invoice.status === 'ready') return `${this.expectedStage ? this.documentLabel : 'Invoice'} is ready to download.`
      if (this.invoice.status === 'pending') return `${this.expectedStage ? this.documentLabel : 'Invoice'} is queued.`
      if (this.invoice.status === 'rendering') return this.expectedStage ? `Generating ${this.documentLabel}.` : 'Generating the invoice PDF.'
      return ''
    },
  },
  watch: {
    // A confirmation changes document identity even when the batch ID is unchanged.
    documentContext: { immediate: true, handler: 'initialize' },
    '$route.params.id'(id) {
      if (String(id) !== String(this.payment.id)) this.invalidate()
    },
  },
  beforeDestroy() { this.invalidate() },
  methods: {
    current(generation) {
      return generation === this.generation && (!this.$route || String(this.$route.params.id) === String(this.payment.id))
    },
    stopObservation() {
      clearTimeout(this.timer)
      clearTimeout(this.deadlineTimer)
      clearTimeout(this.readTimer)
      this.timer = null
      this.deadlineTimer = null
      this.readTimer = null
      this.checkSequence++
      this.checking = false
      this.deadline = 0
    },
    invalidate() {
      this.generation++
      this.stopObservation()
    },
    initialize() {
      this.invalidate()
      const latest = this.payment.latest_invoice_export
      this.invoice = this.matchesStage(latest) ? latest : null
      this.discovering = false
      this.downloading = false
      this.checking = false
      this.postUncertain = false
      this.retryBaseline = null
      this.paused = false
      this.notice = ''
      this.downloadRequested = false
      if (this.active && this.current(this.generation)) {
        this.startObservation()
        this.schedule()
      }
    },
    startObservation() {
      this.stopObservation()
      this.paused = false
      this.notice = ''
      this.pollFailures = 0
      this.deadline = Date.now() + OBSERVATION_LIMIT
      const generation = this.generation
      this.deadlineTimer = setTimeout(() => {
        if (this.current(generation)) this.pauseObservation(WAITING_MESSAGE)
      }, OBSERVATION_LIMIT)
    },
    pauseObservation(message) {
      this.stopObservation()
      this.paused = true
      this.notice = message
    },
    schedule(delay = !this.discovering && this.invoice && this.invoice.status === 'failed' ? 10000 : 3000) {
      clearTimeout(this.timer)
      if (!this.observing || this.paused) return
      if (Date.now() >= this.deadline) {
        this.pauseObservation(WAITING_MESSAGE)
        return
      }
      const generation = this.generation
      this.timer = setTimeout(() => {
        this.timer = null
        if (this.current(generation)) this.poll()
      }, delay)
    },
    matchesStage(invoice) {
      return !!invoice && (!this.expectedStage || invoice.stage === this.expectedStage)
    },
    setInvoice(invoice) {
      if (invoice && (!this.matchesStage(invoice) || !invoice.id || !['not_requested', 'pending', 'rendering', 'ready', 'failed'].includes(invoice.status) ||
        (invoice.batch_id !== undefined && String(invoice.batch_id) !== String(this.payment.id)))) {
        throw new Error('Invalid invoice export response')
      }
      this.invoice = invoice
    },
    async followInvoice(generation) {
      if (this.active) {
        if (!this.deadline) this.startObservation()
        this.schedule()
      } else {
        this.stopObservation()
        if (this.invoice && this.invoice.status === 'ready' && this.downloadRequested) {
          this.downloadRequested = false
          await this.download(generation)
        }
      }
    },
    async notifyError(error, fallback, generation) {
      if (error.response && error.response.status === 401) return
      const message = await invoiceErrorMessage(error, fallback)
      if (!this.current(generation)) return
      this.notice = message
      // Notification titles support HTML, whereas backend details must remain text.
      const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }
      this.$notify({ type: 'error', title: message.replace(/[&<>"']/g, character => entities[character]) })
    },
    async exportInvoice() {
      if (this.disabled || !this.current(this.generation)) return
      if (this.canCheck) {
        await this.checkStatus()
        return
      }
      if (this.invoice && this.invoice.status === 'ready') {
        await this.download(this.generation)
        return
      }
      this.invalidate()
      const generation = this.generation
      this.discovering = true
      this.postUncertain = false
      this.retryBaseline = attemptFingerprint(this.invoice)
      this.downloadRequested = true
      this.startObservation()
      // GET observation starts independently; a slow POST cannot hold up progress.
      postTransferPricingFundingInvoiceExport(this.payment.id).catch(error => this.postFailed(error, generation))
      this.schedule()
    },
    async postFailed(error, generation) {
      // A late POST result must never overwrite a document already found by GET.
      if (!this.current(generation) || !this.discovering) return
      const status = error.response && error.response.status
      if (!status || status === 408 || status >= 500) {
        this.postUncertain = true
      } else {
        this.stopObservation()
        this.discovering = false
        this.paused = false
        this.downloadRequested = false
        await this.notifyError(error, 'Unable to export the invoice. Try again later.', generation)
      }
    },
    async checkStatus() {
      if (this.busy || !this.current(this.generation)) return
      if (this.discovering || this.invoice) {
        this.startObservation()
        await this.poll()
      }
    },
    async poll() {
      const generation = this.generation
      if (this.checking || this.paused || !this.observing || !this.current(generation)) return
      if (Date.now() >= this.deadline) {
        this.pauseObservation(WAITING_MESSAGE)
        return
      }
      const checkSequence = ++this.checkSequence
      this.checking = true
      try {
        const hasDocument = !!this.invoice
        const request = hasDocument ? getTransferPricingFundingInvoiceExport(this.invoice.id) : getTransferPricingFundingInvoiceBatch(this.payment.id)
        const timeout = new Promise((resolve, reject) => {
          this.readTimer = setTimeout(() => reject(new Error('Invoice status request timed out')), STATUS_REQUEST_TIMEOUT)
        })
        const { data } = await Promise.race([request, timeout])
        if (!this.current(generation) || checkSequence !== this.checkSequence || this.paused) return
        const received = hasDocument ? data : data.latest_invoice_export
        const candidate = this.matchesStage(received) ? received : null
        this.pollFailures = 0
        this.notice = ''
        // A GET can precede POST commit, or still contain the previous failed attempt.
        if (this.discovering && (!candidate || attemptFingerprint(candidate) === this.retryBaseline)) {
          if (this.postUncertain) {
            this.discovering = false
            this.downloadRequested = false
            this.stopObservation()
            if (!candidate) this.notice = 'No invoice was found. You can try Export again.'
          } else this.schedule()
          return
        }
        if (!candidate) throw new Error('Invalid invoice export response')
        this.setInvoice(candidate)
        this.discovering = false
        this.retryBaseline = null
        await this.followInvoice(generation)
      } catch (error) {
        if (!this.current(generation) || checkSequence !== this.checkSequence || this.paused) return
        const status = error.response && error.response.status
        const retryable = !status || status === 408 || status === 429 || status >= 500
        if (retryable && this.pollFailures < RETRY_DELAYS.length) {
          this.notice = 'Invoice status is temporarily unavailable. Retrying…'
          const delay = RETRY_DELAYS[this.pollFailures++]
          this.schedule(delay)
        } else this.pauseObservation(`Unable to check invoice status. ${WAITING_MESSAGE}`)
      } finally {
        if (this.current(generation) && checkSequence === this.checkSequence) {
          clearTimeout(this.readTimer)
          this.readTimer = null
          this.checking = false
        }
      }
    },
    async download(generation) {
      if (this.downloading || !this.current(generation)) return
      this.downloading = true
      this.notice = ''
      const invoice = this.invoice
      try {
        const response = await getTransferPricingFundingInvoiceFile(invoice.id)
        if (!this.current(generation)) return
        const blob = await invoicePdf(response)
        if (!this.current(generation)) return
        downloadInvoicePdf(blob, this.payment, invoice)
      } catch (error) {
        if (this.current(generation)) await this.notifyError(error, 'Unable to download the PDF. Use Export to try downloading again.', generation)
      } finally {
        if (this.current(generation)) this.downloading = false
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.funding-invoice-export
  margin-left: auto

.funding-invoice-export__status
  max-width: 360px
  color: $color-text-second
  font-size: 13px
</style>
